<template>
  <div>
    <!-- Pet Care -->
    <tag-label-group v-if="petCareServices.length" :items="petCareServices" class="mt-16" />

    <!-- Child Care (Babysitter/Nanny) -->
    <div v-if="isChildCare">
      <div v-if="infantCareServices.length || infantCareExperienceText">
        <tag-label-group :items="infantCareServices" class="mt-16" title="Infant care services">
          <template v-if="infantCareExperienceText" #subtitle>
            <p class="text-small text-grey-text-subdued">{{ infantCareExperienceText }}</p>
          </template>
        </tag-label-group>
      </div>
      <tag-label-group v-if="childCareServices.length" :items="childCareServices" class="mt-16" title="General Services" />
    </div>

    <!-- Companion Care -->
    <div v-if="careType === 'companionCare'">
      <tag-label-group v-if="companionCareGeneral.length" :items="companionCareGeneral" class="mt-16" title="General services" />
      <tag-label-group
        v-if="companionCareIndividualized.length"
        :items="companionCareIndividualized"
        class="mt-16"
        title="Individualized services"
      />
      <tag-label-group
        v-if="companionCareHealth.length"
        :items="companionCareHealth"
        class="mt-16"
        title="Health & medical services"
      />
    </div>

    <!-- Housekeeping -->
    <tag-label-group v-if="housekeepingServices.length" :items="housekeepingServices" class="mt-16" />
  </div>
</template>

<script lang="ts">
import pluralize from 'pluralize';
import TagLabelGroup from '@/components/tag_label_group/TagLabelGroup.vue';
import {
  ServiceDisplay,
  mapPetCareServices,
  mapChildCareServices,
  mapInfantCareServices,
  mapInfantCareExperience,
  mapCompanionCareServices,
  mapHousekeepingServices,
  PetCareServices,
  ChildCareServices,
  CompanionCareServices,
  HousekeepingServices,
} from '../../__types__/ServiceTypes';
import { FormattedCareType, isChildCare } from '../../__types__/CareTypes';

export default {
  name: 'ProfileServices',
  components: { TagLabelGroup },
  props: {
    careType: {
      type: String as () => FormattedCareType,
      required: true,
    },
    services: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isChildCare(): boolean {
      return isChildCare(this.careType);
    },

    petCareServices(): ServiceDisplay[] {
      if (this.careType !== 'petCare') return [];
      return mapPetCareServices(this.services as PetCareServices);
    },

    housekeepingServices(): ServiceDisplay[] {
      if (this.careType !== 'housekeeping') return [];
      return mapHousekeepingServices(this.services as HousekeepingServices);
    },

    childCareServices(): ServiceDisplay[] {
      if (!this.isChildCare) return [];
      return mapChildCareServices(this.services as ChildCareServices);
    },

    infantCareServices(): ServiceDisplay[] {
      if (!this.isChildCare) return [];
      const services = this.services as ChildCareServices;
      return mapInfantCareServices(services.infantCareServices || []);
    },

    infantCareExperienceText(): string {
      if (!this.isChildCare) return '';
      const services = this.services as ChildCareServices;
      const experience = services.infantCareExperience;
      if (!experience) return '';

      const display = mapInfantCareExperience(experience);
      if (display) return `${display} of experience`;

      return `${experience} ${pluralize('year', parseInt(experience, 10))} of experience`;
    },

    companionCareGeneral(): ServiceDisplay[] {
      if (this.careType !== 'companionCare') return [];
      return mapCompanionCareServices(this.services as CompanionCareServices, 'general');
    },

    companionCareIndividualized(): ServiceDisplay[] {
      if (this.careType !== 'companionCare') return [];
      return mapCompanionCareServices(this.services as CompanionCareServices, 'individualized');
    },

    companionCareHealth(): ServiceDisplay[] {
      if (this.careType !== 'companionCare') return [];
      return mapCompanionCareServices(this.services as CompanionCareServices, 'health');
    },
  },
};
</script>
